/**
 * The function library.
 *
 * Arguments arrive as thunks rather than values. Nearly every function forces
 * them immediately, but `IF` and `IFERROR` must not: `=IF(A1=0,"n/a",100/A1)`
 * has to return "n/a" when A1 is zero, and it cannot do that if the division
 * has already been evaluated and blown up. Uniform laziness is cheaper than a
 * special case in the evaluator.
 *
 * Two coercion rules run through everything, and they differ on purpose
 * because Excel's do:
 *
 *   - Aggregates over a *range* skip text and blanks. `=SUM(A1:A9)` down a
 *     ledger column must not break because one row holds a note.
 *   - Arithmetic on a *scalar* does not skip anything. `=A1+A2` where A2 is
 *     text is `#VALUE!`, because that is a mistake, not a note.
 */
import * as D from "./decimal.js";
// Cycle-safe: number-pattern reads serialToDate from here, and TEXT below
// only calls renderPattern at evaluation time, long after both are loaded.
import { renderPattern } from "./number-pattern.js";
import { BLANK, booleanValue, errorValue, isError, numberValue, textValue, } from "./types.js";
// ---------------------------------------------------------------------------
// Coercion
// ---------------------------------------------------------------------------
function firstValue(argument) {
    return argument.kind === 'value' ? argument.value : (argument.values[0] ?? BLANK);
}
function flatten(args) {
    const out = [];
    for (const thunk of args) {
        const argument = thunk();
        if (argument.kind === 'value')
            out.push(argument.value);
        else
            out.push(...argument.values);
    }
    return out;
}
/**
 * Flatten, leaving out cells that are themselves SUBTOTAL formulas.
 *
 * The whole reason SUBTOTAL exists. A grand total written over a column that
 * already carries monthly subtotals has to skip them; SUM there comes out at
 * twice the year and looks entirely right, which is the worst kind of wrong a
 * ledger can be.
 *
 * A range with no flags — one built by a caller that could not work them out —
 * is taken whole, so SUBTOTAL degrades to its plain equivalent rather than
 * silently dropping figures.
 */
function flattenOuter(args) {
    const out = [];
    for (const thunk of args) {
        const argument = thunk();
        if (argument.kind === 'value') {
            out.push(argument.value);
            continue;
        }
        const nested = argument.nested;
        argument.values.forEach((value, index) => {
            if (nested?.[index])
                return;
            out.push(value);
        });
    }
    return out;
}
const ok = (value) => ({ ok: true, value });
const fail = (code) => ({ ok: false, code });
/** Strict scalar coercion, for arithmetic. */
export function toNumber(value) {
    switch (value.kind) {
        case 'number':
            return ok(value.value);
        case 'blank':
            return ok(D.ZERO);
        case 'boolean':
            return ok(value.value ? D.ONE : D.ZERO);
        case 'error':
            return fail(value.code);
        case 'text': {
            const parsed = D.fromString(value.value);
            return parsed ? ok(parsed) : fail('#VALUE!');
        }
    }
}
export function toText(value) {
    switch (value.kind) {
        case 'text':
            return ok(value.value);
        case 'number':
            return ok(D.toString(value.value));
        case 'boolean':
            return ok(value.value ? 'TRUE' : 'FALSE');
        case 'blank':
            return ok('');
        case 'error':
            return fail(value.code);
    }
}
export function toBoolean(value) {
    switch (value.kind) {
        case 'boolean':
            return ok(value.value);
        case 'number':
            return ok(!D.isZero(value.value));
        case 'blank':
            return ok(false);
        case 'error':
            return fail(value.code);
        case 'text': {
            const upper = value.value.trim().toUpperCase();
            if (upper === 'TRUE')
                return ok(true);
            if (upper === 'FALSE')
                return ok(false);
            return fail('#VALUE!');
        }
    }
}
/** Numbers for aggregation: text and blanks are skipped, errors propagate. */
function collectNumbers(values) {
    const numbers = [];
    for (const value of values) {
        if (isError(value))
            return fail(value.code);
        if (value.kind === 'number')
            numbers.push(value.value);
        else if (value.kind === 'boolean')
            numbers.push(value.value ? D.ONE : D.ZERO);
        // text and blank: skipped, as Excel does
    }
    return ok(numbers);
}
// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------
/**
 * Excel's day zero. Serial 1 is 1900-01-01, and the sequence is offset by one
 * because Excel believes 1900 was a leap year. Anchoring at 1899-12-30 gives
 * the right answer for every date from 1900-03-01 onward, which is every date
 * this system will ever hold.
 */
const EPOCH = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86_400_000;
export function dateToSerial(date) {
    const utc = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    return Math.round((utc - EPOCH) / MS_PER_DAY);
}
export function serialToDate(serial) {
    return new Date(EPOCH + Math.round(serial) * MS_PER_DAY);
}
function parseCriterion(raw) {
    const text = toText(raw);
    const source = text.ok ? text.value : '';
    const match = /^(<>|<=|>=|<|>|=)?(.*)$/.exec(source.trim());
    const operator = match?.[1] ?? '';
    const operand = match?.[2] ?? '';
    const asNumber = D.fromString(operand);
    return {
        test(value) {
            if (asNumber) {
                const candidate = value.kind === 'number' ? value.value : null;
                if (!candidate)
                    return operator === '<>';
                const order = D.compare(candidate, asNumber);
                switch (operator) {
                    case '<':
                        return order < 0;
                    case '<=':
                        return order <= 0;
                    case '>':
                        return order > 0;
                    case '>=':
                        return order >= 0;
                    case '<>':
                        return order !== 0;
                    default:
                        return order === 0;
                }
            }
            const candidate = toText(value);
            const left = (candidate.ok ? candidate.value : '').trim().toUpperCase();
            const right = operand.trim().toUpperCase();
            return operator === '<>' ? left !== right : left === right;
        },
    };
}
// ---------------------------------------------------------------------------
// Helpers for defining functions
// ---------------------------------------------------------------------------
function define(minArgs, maxArgs, call) {
    return { minArgs, maxArgs, call };
}
/** Aggregate over every value in every argument. */
function aggregate(reduce) {
    return define(1, Infinity, (args) => {
        const numbers = collectNumbers(flatten(args));
        if (!numbers.ok)
            return errorValue(numbers.code);
        return reduce(numbers.value);
    });
}
/** One scalar in, one scalar out, with error propagation handled centrally. */
function numeric(minArgs, maxArgs, compute) {
    return define(minArgs, maxArgs, (args) => {
        const numbers = [];
        for (const thunk of args) {
            const value = firstValue(thunk());
            const converted = toNumber(value);
            if (!converted.ok)
                return errorValue(converted.code);
            numbers.push(converted.value);
        }
        return compute(numbers);
    });
}
function requireInt(value, fallback) {
    if (!value)
        return fallback;
    return Number(D.toString(D.truncate(value)));
}
/** Integer square root by Newton's method, then scaled back down. Exact for
 *  perfect squares, correctly rounded otherwise. */
function sqrtDec(value) {
    if (value.u < 0n)
        return null;
    if (value.u === 0n)
        return D.ZERO;
    const scale = D.DIVISION_SCALE;
    // sqrt(u / 10^s) at `scale` places = sqrt(u * 10^(2*scale - s)) / 10^scale
    const shift = 2 * scale - value.s;
    if (shift < 0)
        return null;
    const target = value.u * 10n ** BigInt(shift);
    let guess = target;
    let previous = 0n;
    while (guess !== previous) {
        previous = guess;
        guess = (guess + target / guess) / 2n;
        if (guess > previous)
            break; // converged and oscillating by one
    }
    return D.normalise({ u: guess, s: scale });
}
// ---------------------------------------------------------------------------
// The library
// ---------------------------------------------------------------------------
export const FUNCTIONS = {
    SUM: aggregate((numbers) => numberValue(numbers.reduce(D.add, D.ZERO))),
    PRODUCT: aggregate((numbers) => numbers.length === 0 ? numberValue(D.ZERO) : numberValue(numbers.reduce(D.multiply, D.ONE))),
    AVERAGE: aggregate((numbers) => {
        if (numbers.length === 0)
            return errorValue('#DIV/0!');
        const total = numbers.reduce(D.add, D.ZERO);
        const mean = D.divide(total, D.fromInt(numbers.length));
        return mean ? numberValue(mean) : errorValue('#DIV/0!');
    }),
    MIN: aggregate((numbers) => numbers.length === 0
        ? numberValue(D.ZERO)
        : numberValue(numbers.reduce((a, b) => (D.compare(b, a) < 0 ? b : a)))),
    MAX: aggregate((numbers) => numbers.length === 0
        ? numberValue(D.ZERO)
        : numberValue(numbers.reduce((a, b) => (D.compare(b, a) > 0 ? b : a)))),
    /**
     * SUBTOTAL(function, range…) — an aggregate that ignores other subtotals.
     *
     * Excel's function numbers: 1 average, 2 count, 3 counta, 4 max, 5 min,
     * 6 product, 9 sum. The 101–109 forms mean the same thing while also
     * ignoring rows hidden by hand; rows hidden by an outline are skipped by
     * both, and hiding a row by hand to change a total is not a thing this
     * app's users do, so the two behave alike here rather than differently and
     * subtly wrong.
     */
    SUBTOTAL: define(2, Infinity, (args) => {
        const which = firstValue(args[0]());
        const code = toNumber(which);
        if (!code.ok)
            return errorValue(code.code);
        const fn = Math.trunc(D.toNumber(code.value)) % 100;
        const rest = args.slice(1);
        const values = flattenOuter(rest);
        if (fn === 3)
            return numberValue(D.fromInt(values.filter((v) => v.kind !== 'blank').length));
        if (fn === 2)
            return numberValue(D.fromInt(values.filter((v) => v.kind === 'number').length));
        const numbers = collectNumbers(values);
        if (!numbers.ok)
            return errorValue(numbers.code);
        const found = numbers.value;
        switch (fn) {
            case 1:
                return found.length === 0
                    ? errorValue('#DIV/0!')
                    : numberValue(D.divide(found.reduce(D.add, D.ZERO), D.fromInt(found.length)) ?? D.ZERO);
            case 4:
                return found.length === 0
                    ? errorValue('#NUM!')
                    : numberValue(found.reduce((best, v) => (D.compare(v, best) > 0 ? v : best)));
            case 5:
                return found.length === 0
                    ? errorValue('#NUM!')
                    : numberValue(found.reduce((best, v) => (D.compare(v, best) < 0 ? v : best)));
            case 6:
                return numberValue(found.reduce(D.multiply, D.fromInt(1)));
            case 9:
                return numberValue(found.reduce(D.add, D.ZERO));
            default:
                // An unknown function number is a mistake in the formula, not a
                // reason to guess at SUM.
                return errorValue('#VALUE!');
        }
    }),
    COUNT: define(1, Infinity, (args) => {
        const count = flatten(args).filter((value) => value.kind === 'number').length;
        return numberValue(D.fromInt(count));
    }),
    COUNTA: define(1, Infinity, (args) => {
        const count = flatten(args).filter((value) => value.kind !== 'blank').length;
        return numberValue(D.fromInt(count));
    }),
    COUNTBLANK: define(1, Infinity, (args) => {
        const count = flatten(args).filter((value) => value.kind === 'blank').length;
        return numberValue(D.fromInt(count));
    }),
    /**
     * Coerce to a number, treating text and blanks as zero.
     *
     * The reason this exists: it lets a running-balance column use one formula
     * for every row including the first, because `N(F5)` over a header cell is
     * 0 rather than #VALUE!. A uniform column is what lets an inserted row
     * inherit a formula that actually chains.
     */
    N: define(1, 1, (args) => {
        const value = firstValue(args[0]());
        if (isError(value))
            return value;
        if (value.kind === 'number')
            return value;
        if (value.kind === 'boolean')
            return numberValue(value.value ? D.ONE : D.ZERO);
        return numberValue(D.ZERO);
    }),
    ABS: numeric(1, 1, ([value]) => numberValue(D.abs(value))),
    SIGN: numeric(1, 1, ([value]) => {
        const order = D.compare(value, D.ZERO);
        return numberValue(D.fromInt(order));
    }),
    INT: numeric(1, 1, ([value]) => numberValue(D.floor(value))),
    TRUNC: numeric(1, 2, ([value, places]) => numberValue(D.truncate(value, requireInt(places, 0)))),
    ROUND: numeric(2, 2, ([value, places]) => numberValue(D.round(value, requireInt(places, 0)))),
    ROUNDUP: numeric(2, 2, ([value, places]) => {
        const digits = requireInt(places, 0);
        const truncated = D.truncate(value, digits);
        if (D.compare(truncated, value) === 0)
            return numberValue(truncated);
        // Away from zero, by one unit in the last kept place.
        const step = D.divide(D.ONE, D.power(D.fromInt(10), D.fromInt(digits)) ?? D.ONE);
        if (!step)
            return errorValue('#NUM!');
        const away = value.u < 0n ? D.subtract(truncated, step) : D.add(truncated, step);
        return numberValue(away);
    }),
    ROUNDDOWN: numeric(2, 2, ([value, places]) => numberValue(D.truncate(value, requireInt(places, 0)))),
    MOD: numeric(2, 2, ([dividend, divisor]) => {
        if (D.isZero(divisor))
            return errorValue('#DIV/0!');
        const quotient = D.divide(dividend, divisor);
        if (!quotient)
            return errorValue('#DIV/0!');
        // Excel's MOD takes the sign of the divisor, so it uses floor, not trunc.
        const whole = D.floor(quotient);
        return numberValue(D.subtract(dividend, D.multiply(whole, divisor)));
    }),
    POWER: numeric(2, 2, ([base, exponent]) => {
        const result = D.power(base, exponent);
        return result ? numberValue(result) : errorValue('#NUM!');
    }),
    SQRT: numeric(1, 1, ([value]) => {
        const result = sqrtDec(value);
        return result ? numberValue(result) : errorValue('#NUM!');
    }),
    IF: define(2, 3, (args) => {
        const condition = toBoolean(firstValue(args[0]()));
        if (!condition.ok)
            return errorValue(condition.code);
        if (condition.value)
            return firstValue(args[1]());
        return args[2] ? firstValue(args[2]()) : booleanValue(false);
    }),
    IFERROR: define(2, 2, (args) => {
        const value = firstValue(args[0]());
        return isError(value) ? firstValue(args[1]()) : value;
    }),
    AND: define(1, Infinity, (args) => {
        for (const value of flatten(args)) {
            if (value.kind === 'blank')
                continue;
            const flag = toBoolean(value);
            if (!flag.ok)
                return errorValue(flag.code);
            if (!flag.value)
                return booleanValue(false);
        }
        return booleanValue(true);
    }),
    OR: define(1, Infinity, (args) => {
        for (const value of flatten(args)) {
            if (value.kind === 'blank')
                continue;
            const flag = toBoolean(value);
            if (!flag.ok)
                return errorValue(flag.code);
            if (flag.value)
                return booleanValue(true);
        }
        return booleanValue(false);
    }),
    NOT: define(1, 1, (args) => {
        const flag = toBoolean(firstValue(args[0]()));
        return flag.ok ? booleanValue(!flag.value) : errorValue(flag.code);
    }),
    SUMIF: define(2, 3, (args) => {
        const range = args[0]();
        const criterion = parseCriterion(firstValue(args[1]()));
        const sumRange = args[2] ? args[2]() : range;
        const tested = range.kind === 'range' ? range.values : [range.value];
        const summed = sumRange.kind === 'range' ? sumRange.values : [sumRange.value];
        let total = D.ZERO;
        for (let i = 0; i < tested.length; i += 1) {
            const candidate = tested[i];
            if (isError(candidate))
                return errorValue(candidate.code);
            if (!criterion.test(candidate))
                continue;
            const target = summed[i];
            if (target?.kind === 'number')
                total = D.add(total, target.value);
        }
        return numberValue(total);
    }),
    COUNTIF: define(2, 2, (args) => {
        const range = args[0]();
        const criterion = parseCriterion(firstValue(args[1]()));
        const values = range.kind === 'range' ? range.values : [range.value];
        const count = values.filter((value) => !isError(value) && criterion.test(value)).length;
        return numberValue(D.fromInt(count));
    }),
    CONCAT: define(1, Infinity, (args) => {
        let out = '';
        for (const value of flatten(args)) {
            const text = toText(value);
            if (!text.ok)
                return errorValue(text.code);
            out += text.value;
        }
        return textValue(out);
    }),
    LEN: define(1, 1, (args) => {
        const text = toText(firstValue(args[0]()));
        return text.ok ? numberValue(D.fromInt(text.value.length)) : errorValue(text.code);
    }),
    LEFT: define(1, 2, (args) => {
        const text = toText(firstValue(args[0]()));
        if (!text.ok)
            return errorValue(text.code);
        const count = args[1] ? toNumber(firstValue(args[1]())) : ok(D.ONE);
        if (!count.ok)
            return errorValue(count.code);
        return textValue(text.value.slice(0, Math.max(0, requireInt(count.value, 1))));
    }),
    RIGHT: define(1, 2, (args) => {
        const text = toText(firstValue(args[0]()));
        if (!text.ok)
            return errorValue(text.code);
        const count = args[1] ? toNumber(firstValue(args[1]())) : ok(D.ONE);
        if (!count.ok)
            return errorValue(count.code);
        const take = Math.max(0, requireInt(count.value, 1));
        return textValue(take === 0 ? '' : text.value.slice(-take));
    }),
    MID: define(3, 3, (args) => {
        const text = toText(firstValue(args[0]()));
        if (!text.ok)
            return errorValue(text.code);
        const start = toNumber(firstValue(args[1]()));
        const count = toNumber(firstValue(args[2]()));
        if (!start.ok)
            return errorValue(start.code);
        if (!count.ok)
            return errorValue(count.code);
        const from = requireInt(start.value, 1) - 1; // MID is one-based
        if (from < 0)
            return errorValue('#VALUE!');
        return textValue(text.value.slice(from, from + Math.max(0, requireInt(count.value, 0))));
    }),
    /**
     * TEXT(value, code) — a number written the way a format code says.
     *
     * Excel's own way of putting a unit beside a figure inside a formula:
     * TEXT(A1,"0 \"kg\"") gives "25 kg". The other two ways are a custom
     * number format, which keeps the cell numeric and is usually the better
     * answer, and plain concatenation with &, which cannot format anything.
     *
     * The code is read by the same interpreter the Custom tab uses, so
     * whatever can be typed there works here. A code it cannot read gives
     * #VALUE! rather than something that looks right and is not.
     */
    TEXT: define(2, 2, (args) => {
        const value = firstValue(args[0]());
        const code = toText(firstValue(args[1]()));
        if (!code.ok)
            return errorValue(code.code);
        if (value.kind === 'error')
            return value;
        const drawn = renderPattern(value, code.value);
        return drawn ? textValue(drawn.text) : errorValue('#VALUE!');
    }),
    UPPER: define(1, 1, (args) => {
        const text = toText(firstValue(args[0]()));
        return text.ok ? textValue(text.value.toUpperCase()) : errorValue(text.code);
    }),
    LOWER: define(1, 1, (args) => {
        const text = toText(firstValue(args[0]()));
        return text.ok ? textValue(text.value.toLowerCase()) : errorValue(text.code);
    }),
    TRIM: define(1, 1, (args) => {
        const text = toText(firstValue(args[0]()));
        return text.ok ? textValue(text.value.trim().replace(/\s+/g, ' ')) : errorValue(text.code);
    }),
    /**
     * Where one piece of text sits inside another, counting from one.
     *
     * SEARCH ignores case and FIND does not, which is the whole difference
     * between them in Excel. Both answer #VALUE! when the text is absent, and
     * that refusal is load-bearing: `ISERROR(SEARCH(…))` is how a spreadsheet
     * asks "does this cell not contain that", including in the formulas a
     * conditional-formatting rule exports as.
     */
    SEARCH: define(2, 3, (args) => {
        const needle = toText(firstValue(args[0]()));
        const haystack = toText(firstValue(args[1]()));
        if (!needle.ok)
            return errorValue(needle.code);
        if (!haystack.ok)
            return errorValue(haystack.code);
        const from = args[2] ? toNumber(firstValue(args[2]())) : null;
        const start = from && from.ok ? Math.max(1, Math.trunc(D.toNumber(from.value))) : 1;
        const at = haystack.value.toLowerCase().indexOf(needle.value.toLowerCase(), start - 1);
        return at < 0 ? errorValue('#VALUE!') : numberValue(D.fromInt(at + 1));
    }),
    FIND: define(2, 3, (args) => {
        const needle = toText(firstValue(args[0]()));
        const haystack = toText(firstValue(args[1]()));
        if (!needle.ok)
            return errorValue(needle.code);
        if (!haystack.ok)
            return errorValue(haystack.code);
        const from = args[2] ? toNumber(firstValue(args[2]())) : null;
        const start = from && from.ok ? Math.max(1, Math.trunc(D.toNumber(from.value))) : 1;
        const at = haystack.value.indexOf(needle.value, start - 1);
        return at < 0 ? errorValue('#VALUE!') : numberValue(D.fromInt(at + 1));
    }),
    ISERROR: define(1, 1, (args) => booleanValue(isError(firstValue(args[0]())))),
    ISNUMBER: define(1, 1, (args) => booleanValue(firstValue(args[0]()).kind === 'number')),
    ISTEXT: define(1, 1, (args) => booleanValue(firstValue(args[0]()).kind === 'text')),
    ISBLANK: define(1, 1, (args) => booleanValue(firstValue(args[0]()).kind === 'blank')),
    TODAY: define(0, 0, () => numberValue(D.fromInt(dateToSerial(new Date())))),
    DATE: numeric(3, 3, ([year, month, day]) => {
        const y = requireInt(year, 1900);
        const m = requireInt(month, 1);
        const d = requireInt(day, 1);
        const serial = dateToSerial(new Date(Date.UTC(y, m - 1, d)));
        return Number.isFinite(serial) ? numberValue(D.fromInt(serial)) : errorValue('#NUM!');
    }),
    YEAR: numeric(1, 1, ([serial]) => numberValue(D.fromInt(serialToDate(requireInt(serial, 0)).getUTCFullYear()))),
    MONTH: numeric(1, 1, ([serial]) => numberValue(D.fromInt(serialToDate(requireInt(serial, 0)).getUTCMonth() + 1))),
    DAY: numeric(1, 1, ([serial]) => numberValue(D.fromInt(serialToDate(requireInt(serial, 0)).getUTCDate()))),
    // -------------------------------------------------------------------------
    // Lookup and reference
    //
    // The block a ledger cannot do without. Every one of these needs to know how
    // wide the range is, which is why a range argument carries its shape.
    // -------------------------------------------------------------------------
    /**
     * Find a value down the first column and read across.
     *
     * Approximate match is the default in Excel and the cause of most wrong
     * lookups ever written, so it is implemented exactly: the column must be
     * sorted ascending and the largest value not greater than the target wins.
     * Exact match is what people mean, and passing FALSE gets it.
     */
    VLOOKUP: define(3, 4, (args) => {
        const target = firstValue(args[0]());
        if (isError(target))
            return target;
        const table = args[1]();
        if (table.kind !== 'range')
            return errorValue('#VALUE!');
        const index = toNumber(firstValue(args[2]()));
        if (!index.ok)
            return errorValue(index.code);
        const column = Math.trunc(D.toNumber(index.value));
        if (column < 1 || column > table.columns)
            return errorValue('#REF!');
        const approximate = args[3] ? asFlag(firstValue(args[3]())) : true;
        if (approximate === null)
            return errorValue('#VALUE!');
        const row = searchColumn(table, 0, target, approximate);
        if (row === null)
            return errorValue('#N/A');
        return table.values[row * table.columns + (column - 1)] ?? BLANK;
    }),
    /** The same, along the first row and down. */
    HLOOKUP: define(3, 4, (args) => {
        const target = firstValue(args[0]());
        if (isError(target))
            return target;
        const table = args[1]();
        if (table.kind !== 'range')
            return errorValue('#VALUE!');
        const index = toNumber(firstValue(args[2]()));
        if (!index.ok)
            return errorValue(index.code);
        const row = Math.trunc(D.toNumber(index.value));
        if (row < 1 || row > table.rows)
            return errorValue('#REF!');
        const approximate = args[3] ? asFlag(firstValue(args[3]())) : true;
        if (approximate === null)
            return errorValue('#VALUE!');
        const column = searchRow(table, 0, target, approximate);
        if (column === null)
            return errorValue('#N/A');
        return table.values[(row - 1) * table.columns + column] ?? BLANK;
    }),
    /**
     * The cell at a row and column of a block.
     *
     * A zero means "the whole of that axis", which Excel uses for array work.
     * There is no array machinery here, so a zero on a block more than one cell
     * wide or tall is refused rather than quietly returning its first cell.
     */
    INDEX: define(2, 3, (args) => {
        const table = args[0]();
        if (table.kind !== 'range') {
            // A single cell is a one-by-one block, and INDEX(cell,1,1) is legal.
            return firstValue(table);
        }
        const rowArg = toNumber(firstValue(args[1]()));
        if (!rowArg.ok)
            return errorValue(rowArg.code);
        let row = Math.trunc(D.toNumber(rowArg.value));
        let column = 1;
        if (args[2]) {
            const columnArg = toNumber(firstValue(args[2]()));
            if (!columnArg.ok)
                return errorValue(columnArg.code);
            column = Math.trunc(D.toNumber(columnArg.value));
        }
        else if (table.rows === 1) {
            // INDEX(A1:F1, 3) means the third cell along, not the third row.
            column = row;
            row = 1;
        }
        if (row === 0) {
            if (table.rows !== 1)
                return errorValue('#VALUE!');
            row = 1;
        }
        if (column === 0) {
            if (table.columns !== 1)
                return errorValue('#VALUE!');
            column = 1;
        }
        if (row < 1 || row > table.rows || column < 1 || column > table.columns) {
            return errorValue('#REF!');
        }
        return table.values[(row - 1) * table.columns + (column - 1)] ?? BLANK;
    }),
    /**
     * Where a value sits in a single row or column.
     *
     * The third argument is the match type Excel uses: 1 (or absent) wants the
     * list sorted ascending and takes the largest value not greater than the
     * target, 0 is exact, and -1 wants it descending.
     */
    MATCH: define(2, 3, (args) => {
        const target = firstValue(args[0]());
        if (isError(target))
            return target;
        const range = args[1]();
        const values = range.kind === 'range' ? range.values : [range.value];
        let mode = 1;
        if (args[2]) {
            const raw = toNumber(firstValue(args[2]()));
            if (!raw.ok)
                return errorValue(raw.code);
            mode = Math.trunc(D.toNumber(raw.value));
        }
        const at = matchIn(values, target, mode);
        return at === null ? errorValue('#N/A') : numberValue(D.fromInt(at + 1));
    }),
    /** The nth of the values that follow. */
    CHOOSE: define(2, Infinity, (args) => {
        const index = toNumber(firstValue(args[0]()));
        if (!index.ok)
            return errorValue(index.code);
        const at = Math.trunc(D.toNumber(index.value));
        const chosen = args[at];
        if (at < 1 || !chosen)
            return errorValue('#VALUE!');
        return firstValue(chosen());
    }),
    ROWS: define(1, 1, (args) => {
        const range = args[0]();
        return numberValue(D.fromInt(range.kind === 'range' ? range.rows : 1));
    }),
    COLUMNS: define(1, 1, (args) => {
        const range = args[0]();
        return numberValue(D.fromInt(range.kind === 'range' ? range.columns : 1));
    }),
    // -------------------------------------------------------------------------
    // Conditional aggregates
    // -------------------------------------------------------------------------
    /** SUMIF over several conditions at once, all of which must hold. */
    SUMIFS: define(3, Infinity, (args) => {
        const summed = args[0]();
        const target = summed.kind === 'range' ? summed.values : [summed.value];
        const rows = matchingRows(args.slice(1), target.length);
        if (!rows.ok)
            return errorValue(rows.code);
        let total = D.ZERO;
        for (const index of rows.value) {
            const value = target[index];
            if (value?.kind === 'number')
                total = D.add(total, value.value);
        }
        return numberValue(total);
    }),
    COUNTIFS: define(2, Infinity, (args) => {
        const first = args[0]();
        const length = first.kind === 'range' ? first.values.length : 1;
        const rows = matchingRows(args, length);
        if (!rows.ok)
            return errorValue(rows.code);
        return numberValue(D.fromInt(rows.value.length));
    }),
    /** The mean of the rows that match, which is #DIV/0! when none do. */
    AVERAGEIF: define(2, 3, (args) => {
        const range = args[0]();
        const criterion = parseCriterion(firstValue(args[1]()));
        const source = args[2] ? args[2]() : range;
        const tested = range.kind === 'range' ? range.values : [range.value];
        const averaged = source.kind === 'range' ? source.values : [source.value];
        const numbers = [];
        for (let i = 0; i < tested.length; i += 1) {
            const candidate = tested[i];
            if (isError(candidate))
                return errorValue(candidate.code);
            if (!criterion.test(candidate))
                continue;
            const value = averaged[i];
            if (value?.kind === 'number')
                numbers.push(value.value);
        }
        return meanOf(numbers);
    }),
    AVERAGEIFS: define(3, Infinity, (args) => {
        const source = args[0]();
        const averaged = source.kind === 'range' ? source.values : [source.value];
        const rows = matchingRows(args.slice(1), averaged.length);
        if (!rows.ok)
            return errorValue(rows.code);
        const numbers = [];
        for (const index of rows.value) {
            const value = averaged[index];
            if (value?.kind === 'number')
                numbers.push(value.value);
        }
        return meanOf(numbers);
    }),
    /** Multiply across, then add up — how a line-total column is done in one cell. */
    SUMPRODUCT: define(1, Infinity, (args) => {
        const blocks = args.map((thunk) => {
            const argument = thunk();
            return argument.kind === 'range' ? argument.values : [argument.value];
        });
        const length = blocks[0]?.length ?? 0;
        if (blocks.some((block) => block.length !== length))
            return errorValue('#VALUE!');
        let total = D.ZERO;
        for (let i = 0; i < length; i += 1) {
            let product = D.ONE;
            for (const block of blocks) {
                const value = block[i];
                if (value && isError(value))
                    return errorValue(value.code);
                // Text and blanks count as zero here, as Excel does, so a note in a
                // column does not turn the whole total into #VALUE!.
                product = D.multiply(product, value?.kind === 'number' ? value.value : D.ZERO);
            }
            total = D.add(total, product);
        }
        return numberValue(total);
    }),
    // -------------------------------------------------------------------------
    // Statistical
    // -------------------------------------------------------------------------
    MEDIAN: aggregate((numbers) => {
        if (numbers.length === 0)
            return errorValue('#NUM!');
        const sorted = [...numbers].sort(D.compare);
        const middle = Math.floor(sorted.length / 2);
        if (sorted.length % 2 === 1)
            return numberValue(sorted[middle]);
        const pair = D.add(sorted[middle - 1], sorted[middle]);
        const mean = D.divide(pair, D.fromInt(2));
        return mean ? numberValue(mean) : errorValue('#NUM!');
    }),
    /** The nth largest, counting duplicates separately as Excel does. */
    LARGE: define(2, 2, (args) => nth(args, 'largest')),
    SMALL: define(2, 2, (args) => nth(args, 'smallest')),
    /** Where a number places in a list. Zero or absent ranks largest first. */
    RANK: define(2, 3, (args) => {
        const value = toNumber(firstValue(args[0]()));
        if (!value.ok)
            return errorValue(value.code);
        const range = args[1]();
        const numbers = collectNumbers(range.kind === 'range' ? range.values : [range.value]);
        if (!numbers.ok)
            return errorValue(numbers.code);
        let ascending = false;
        if (args[2]) {
            const order = toNumber(firstValue(args[2]()));
            if (!order.ok)
                return errorValue(order.code);
            ascending = !D.isZero(order.value);
        }
        let place = 1;
        for (const candidate of numbers.value) {
            const side = D.compare(candidate, value.value);
            if (ascending ? side < 0 : side > 0)
                place += 1;
        }
        return numbers.value.some((candidate) => D.compare(candidate, value.value) === 0)
            ? numberValue(D.fromInt(place))
            : errorValue('#N/A');
    }),
    // -------------------------------------------------------------------------
    // Maths
    // -------------------------------------------------------------------------
    /** Round away from zero to a multiple, which is how a price list is tidied. */
    CEILING: numeric(2, 2, ([value, step]) => toMultiple(value, step, 'up')),
    FLOOR: numeric(2, 2, ([value, step]) => toMultiple(value, step, 'down')),
    /** Round to a multiple, nearest wins. */
    MROUND: numeric(2, 2, ([value, step]) => toMultiple(value, step, 'nearest')),
    // -------------------------------------------------------------------------
    // Logical
    // -------------------------------------------------------------------------
    TRUE: define(0, 0, () => booleanValue(true)),
    FALSE: define(0, 0, () => booleanValue(false)),
    /** Pairs of condition and result, first true one wins. */
    IFS: define(2, Infinity, (args) => {
        for (let i = 0; i + 1 < args.length; i += 2) {
            const test = toBoolean(firstValue(args[i]()));
            if (!test.ok)
                return errorValue(test.code);
            if (test.value)
                return firstValue(args[i + 1]());
        }
        return errorValue('#N/A');
    }),
    /** One value against a list of cases, with an optional fallback. */
    SWITCH: define(3, Infinity, (args) => {
        const subject = firstValue(args[0]());
        if (isError(subject))
            return subject;
        let i = 1;
        for (; i + 1 < args.length; i += 2) {
            const candidate = firstValue(args[i]());
            if (sameValue(subject, candidate))
                return firstValue(args[i + 1]());
        }
        // An odd argument left over is the default.
        const fallback = args[args.length - 1];
        return i === args.length - 1 && fallback ? firstValue(fallback()) : errorValue('#N/A');
    }),
    XOR: define(1, Infinity, (args) => {
        let count = 0;
        for (const value of flatten(args)) {
            if (value.kind === 'blank')
                continue;
            const flag = toBoolean(value);
            if (!flag.ok)
                return errorValue(flag.code);
            if (flag.value)
                count += 1;
        }
        return booleanValue(count % 2 === 1);
    }),
    /** Like IFERROR but only for #N/A, which is what a failed lookup gives. */
    IFNA: define(2, 2, (args) => {
        const value = firstValue(args[0]());
        if (isError(value) && value.code === '#N/A')
            return firstValue(args[1]());
        return value;
    }),
    // -------------------------------------------------------------------------
    // Information
    // -------------------------------------------------------------------------
    NA: define(0, 0, () => errorValue('#N/A')),
    ISNA: define(1, 1, (args) => {
        const value = firstValue(args[0]());
        return booleanValue(isError(value) && value.code === '#N/A');
    }),
    ISERR: define(1, 1, (args) => {
        const value = firstValue(args[0]());
        return booleanValue(isError(value) && value.code !== '#N/A');
    }),
    // -------------------------------------------------------------------------
    // Text
    // -------------------------------------------------------------------------
    PROPER: define(1, 1, (args) => {
        const text = toText(firstValue(args[0]()));
        if (!text.ok)
            return errorValue(text.code);
        return textValue(text.value.replace(/[A-Za-z]+/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase()));
    }),
    /** Swap every occurrence, or just the nth. */
    SUBSTITUTE: define(3, 4, (args) => {
        const source = toText(firstValue(args[0]()));
        if (!source.ok)
            return errorValue(source.code);
        const find = toText(firstValue(args[1]()));
        if (!find.ok)
            return errorValue(find.code);
        const replacement = toText(firstValue(args[2]()));
        if (!replacement.ok)
            return errorValue(replacement.code);
        if (find.value === '')
            return textValue(source.value);
        if (!args[3])
            return textValue(source.value.split(find.value).join(replacement.value));
        const which = toNumber(firstValue(args[3]()));
        if (!which.ok)
            return errorValue(which.code);
        const nth = Math.trunc(D.toNumber(which.value));
        if (nth < 1)
            return errorValue('#VALUE!');
        let seen = 0;
        let at = source.value.indexOf(find.value);
        while (at !== -1) {
            seen += 1;
            if (seen === nth) {
                return textValue(source.value.slice(0, at) +
                    replacement.value +
                    source.value.slice(at + find.value.length));
            }
            at = source.value.indexOf(find.value, at + find.value.length);
        }
        return textValue(source.value);
    }),
    /** Swap a run of characters by position, which SUBSTITUTE cannot do. */
    REPLACE: define(4, 4, (args) => {
        const source = toText(firstValue(args[0]()));
        if (!source.ok)
            return errorValue(source.code);
        const start = toNumber(firstValue(args[1]()));
        if (!start.ok)
            return errorValue(start.code);
        const count = toNumber(firstValue(args[2]()));
        if (!count.ok)
            return errorValue(count.code);
        const replacement = toText(firstValue(args[3]()));
        if (!replacement.ok)
            return errorValue(replacement.code);
        const at = Math.trunc(D.toNumber(start.value));
        const length = Math.trunc(D.toNumber(count.value));
        if (at < 1 || length < 0)
            return errorValue('#VALUE!');
        return textValue(source.value.slice(0, at - 1) + replacement.value + source.value.slice(at - 1 + length));
    }),
    REPT: define(2, 2, (args) => {
        const text = toText(firstValue(args[0]()));
        if (!text.ok)
            return errorValue(text.code);
        const times = toNumber(firstValue(args[1]()));
        if (!times.ok)
            return errorValue(times.code);
        const count = Math.trunc(D.toNumber(times.value));
        if (count < 0)
            return errorValue('#VALUE!');
        // A guard rather than a limit anyone will meet: a mistyped reference here
        // would otherwise try to build a string the size of the machine.
        if (count * text.value.length > 32_767)
            return errorValue('#VALUE!');
        return textValue(text.value.repeat(count));
    }),
    /** Read a number out of text, which is what a pasted column needs. */
    VALUE: define(1, 1, (args) => {
        const value = firstValue(args[0]());
        if (isError(value))
            return value;
        if (value.kind === 'number')
            return value;
        const text = toText(value);
        if (!text.ok)
            return errorValue(text.code);
        const parsed = D.fromString(text.value.trim().replace(/,/g, ''));
        return parsed ? numberValue(parsed) : errorValue('#VALUE!');
    }),
    /** Join with a separator, optionally skipping the blanks. */
    TEXTJOIN: define(3, Infinity, (args) => {
        const separator = toText(firstValue(args[0]()));
        if (!separator.ok)
            return errorValue(separator.code);
        const skipBlanks = asFlag(firstValue(args[1]()));
        if (skipBlanks === null)
            return errorValue('#VALUE!');
        const parts = [];
        for (const value of flatten(args.slice(2))) {
            if (isError(value))
                return errorValue(value.code);
            if (skipBlanks && value.kind === 'blank')
                continue;
            const text = toText(value);
            if (!text.ok)
                return errorValue(text.code);
            parts.push(text.value);
        }
        return textValue(parts.join(separator.value));
    }),
    /** Case-sensitive comparison, which the equals sign is not. */
    EXACT: define(2, 2, (args) => {
        const left = toText(firstValue(args[0]()));
        if (!left.ok)
            return errorValue(left.code);
        const right = toText(firstValue(args[1]()));
        if (!right.ok)
            return errorValue(right.code);
        return booleanValue(left.value === right.value);
    }),
    // -------------------------------------------------------------------------
    // Date and time
    // -------------------------------------------------------------------------
    NOW: define(0, 0, () => {
        const now = new Date();
        const days = dateToSerial(now);
        const seconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
        const fraction = D.divide(D.fromInt(seconds), D.fromInt(86_400));
        return numberValue(fraction ? D.add(D.fromInt(days), fraction) : D.fromInt(days));
    }),
    /** The same day a number of months away — how a due date is worked out. */
    EDATE: numeric(2, 2, ([serial, months]) => shiftMonths(requireInt(serial, 0), requireInt(months, 0), 'same day')),
    /** The last day of the month a number of months away. */
    EOMONTH: numeric(2, 2, ([serial, months]) => shiftMonths(requireInt(serial, 0), requireInt(months, 0), 'month end')),
    /** Whole days between two dates, which subtraction already does — named. */
    DAYS: numeric(2, 2, ([later, earlier]) => numberValue(D.subtract(D.fromInt(requireInt(later, 0)), D.fromInt(requireInt(earlier, 0))))),
    /**
     * Sunday is 1 unless told otherwise.
     *
     * Only Excel's two common conventions are implemented — 1 for Sunday-first
     * and 2 for Monday-first. The rest exist and are refused rather than
     * answered wrongly.
     */
    WEEKDAY: numeric(1, 2, ([serial, type]) => {
        const day = serialToDate(requireInt(serial, 0)).getUTCDay();
        const convention = type ? requireInt(type, 1) : 1;
        if (convention === 1)
            return numberValue(D.fromInt(day + 1));
        if (convention === 2)
            return numberValue(D.fromInt(((day + 6) % 7) + 1));
        return errorValue('#NUM!');
    }),
    /** Working days between two dates, weekends excluded. */
    NETWORKDAYS: numeric(2, 2, ([from, to]) => {
        const start = requireInt(from, 0);
        const end = requireInt(to, 0);
        const step = start <= end ? 1 : -1;
        let worked = 0;
        for (let serial = start; step > 0 ? serial <= end : serial >= end; serial += step) {
            const day = serialToDate(serial).getUTCDay();
            if (day !== 0 && day !== 6)
                worked += 1;
        }
        return numberValue(D.fromInt(step > 0 ? worked : -worked));
    }),
    /** The date a number of working days on from another. */
    WORKDAY: numeric(2, 2, ([from, count]) => {
        let serial = requireInt(from, 0);
        let remaining = Math.abs(requireInt(count, 0));
        const step = requireInt(count, 0) < 0 ? -1 : 1;
        while (remaining > 0) {
            serial += step;
            const day = serialToDate(serial).getUTCDay();
            if (day !== 0 && day !== 6)
                remaining -= 1;
        }
        return numberValue(D.fromInt(serial));
    }),
    // -------------------------------------------------------------------------
    // Financial
    //
    // Worked in ordinary floating point rather than the decimal type the rest of
    // the sheet uses. These are all built on compound interest — a power of
    // (1 + rate) with a fractional exponent — which has no exact decimal answer,
    // so pretending otherwise would be false precision. The result is rounded
    // back into a decimal at the end.
    // -------------------------------------------------------------------------
    /** The payment on a loan: rate per period, number of periods, amount. */
    PMT: numeric(3, 5, ([rate, periods, present, future, due]) => {
        const r = D.toNumber(rate);
        const n = D.toNumber(periods);
        const pv = D.toNumber(present);
        const fv = future ? D.toNumber(future) : 0;
        const atStart = due ? D.toNumber(due) !== 0 : false;
        if (n === 0)
            return errorValue('#NUM!');
        if (r === 0)
            return fromFloat(-(pv + fv) / n);
        const growth = Math.pow(1 + r, n);
        const payment = (-r * (pv * growth + fv)) / ((growth - 1) * (atStart ? 1 + r : 1));
        return fromFloat(payment);
    }),
    /** What a stream of payments is worth at the end. */
    FV: numeric(3, 5, ([rate, periods, payment, present, due]) => {
        const r = D.toNumber(rate);
        const n = D.toNumber(periods);
        const pmt = D.toNumber(payment);
        const pv = present ? D.toNumber(present) : 0;
        const atStart = due ? D.toNumber(due) !== 0 : false;
        if (r === 0)
            return fromFloat(-(pv + pmt * n));
        const growth = Math.pow(1 + r, n);
        return fromFloat(-(pv * growth + pmt * (1 + r * (atStart ? 1 : 0)) * ((growth - 1) / r)));
    }),
    /** And what it is worth now. */
    PV: numeric(3, 5, ([rate, periods, payment, future, due]) => {
        const r = D.toNumber(rate);
        const n = D.toNumber(periods);
        const pmt = D.toNumber(payment);
        const fv = future ? D.toNumber(future) : 0;
        const atStart = due ? D.toNumber(due) !== 0 : false;
        if (r === 0)
            return fromFloat(-(fv + pmt * n));
        const growth = Math.pow(1 + r, n);
        return fromFloat(-(fv + pmt * (1 + r * (atStart ? 1 : 0)) * ((growth - 1) / r)) / growth);
    }),
    /** How many payments it takes. */
    NPER: numeric(3, 5, ([rate, payment, present, future, due]) => {
        const r = D.toNumber(rate);
        const pmt = D.toNumber(payment);
        const pv = D.toNumber(present);
        const fv = future ? D.toNumber(future) : 0;
        const atStart = due ? D.toNumber(due) !== 0 : false;
        if (r === 0) {
            if (pmt === 0)
                return errorValue('#NUM!');
            return fromFloat(-(pv + fv) / pmt);
        }
        const adjusted = pmt * (1 + r * (atStart ? 1 : 0));
        const top = adjusted - fv * r;
        const bottom = pv * r + adjusted;
        if (top / bottom <= 0)
            return errorValue('#NUM!');
        return fromFloat(Math.log(top / bottom) / Math.log(1 + r));
    }),
    /**
     * The rate a loan is actually charging, found by bisection.
     *
     * There is no closed form. Excel iterates too; bisection over a wide bracket
     * is slower than its secant method and cannot diverge, which matters more
     * than speed for a cell that recalculates a few times a second.
     */
    RATE: numeric(3, 5, ([periods, payment, present, future, due]) => {
        const n = D.toNumber(periods);
        const pmt = D.toNumber(payment);
        const pv = D.toNumber(present);
        const fv = future ? D.toNumber(future) : 0;
        const atStart = due ? D.toNumber(due) !== 0 : false;
        const balance = (r) => {
            if (r === 0)
                return pv + pmt * n + fv;
            const growth = Math.pow(1 + r, n);
            return pv * growth + pmt * (1 + r * (atStart ? 1 : 0)) * ((growth - 1) / r) + fv;
        };
        let low = -0.999_999;
        let high = 10;
        if (balance(low) * balance(high) > 0)
            return errorValue('#NUM!');
        for (let step = 0; step < 200; step += 1) {
            const middle = (low + high) / 2;
            if (balance(low) * balance(middle) <= 0)
                high = middle;
            else
                low = middle;
        }
        return fromFloat((low + high) / 2);
    }),
    /** The present value of a series of flows, the first one a period away. */
    NPV: define(2, Infinity, (args) => {
        const rate = toNumber(firstValue(args[0]()));
        if (!rate.ok)
            return errorValue(rate.code);
        const r = D.toNumber(rate.value);
        let total = 0;
        let period = 1;
        for (const value of flatten(args.slice(1))) {
            if (isError(value))
                return errorValue(value.code);
            if (value.kind !== 'number')
                continue;
            total += D.toNumber(value.value) / Math.pow(1 + r, period);
            period += 1;
        }
        return fromFloat(total);
    }),
    /** The rate at which a series of flows breaks even. Bisection again. */
    IRR: define(1, 2, (args) => {
        const range = args[0]();
        const values = range.kind === 'range' ? range.values : [range.value];
        const flows = [];
        for (const value of values) {
            if (isError(value))
                return errorValue(value.code);
            if (value.kind === 'number')
                flows.push(D.toNumber(value.value));
        }
        if (flows.length < 2)
            return errorValue('#NUM!');
        const worth = (r) => flows.reduce((total, flow, period) => total + flow / Math.pow(1 + r, period), 0);
        let low = -0.999_999;
        let high = 10;
        if (worth(low) * worth(high) > 0)
            return errorValue('#NUM!');
        for (let step = 0; step < 200; step += 1) {
            const middle = (low + high) / 2;
            if (worth(low) * worth(middle) <= 0)
                high = middle;
            else
                low = middle;
        }
        return fromFloat((low + high) / 2);
    }),
};
// CONCATENATE is the older spelling and still the one most people type.
FUNCTIONS['CONCATENATE'] = FUNCTIONS['CONCAT'];
export function lookupFunction(name) {
    return FUNCTIONS[name.toUpperCase()];
}
export const FUNCTION_NAMES = Object.keys(FUNCTIONS).sort();
// ---------------------------------------------------------------------------
// Helpers for the block above
// ---------------------------------------------------------------------------
/** Excel's TRUE/FALSE flag argument, where a blank means true. */
function asFlag(value) {
    if (value.kind === 'blank')
        return true;
    const flag = toBoolean(value);
    return flag.ok ? flag.value : null;
}
/** Two values equal, in the sense SWITCH and MATCH mean. */
function sameValue(left, right) {
    if (left.kind === 'number' && right.kind === 'number') {
        return D.compare(left.value, right.value) === 0;
    }
    if (left.kind === 'text' && right.kind === 'text') {
        return left.value.toLowerCase() === right.value.toLowerCase();
    }
    if (left.kind === 'boolean' && right.kind === 'boolean')
        return left.value === right.value;
    return left.kind === 'blank' && right.kind === 'blank';
}
/** Ordering for lookup: numbers before text before booleans, as Excel sorts. */
function lookupCompare(left, right) {
    if (left.kind === 'number' && right.kind === 'number')
        return D.compare(left.value, right.value);
    if (left.kind === 'text' && right.kind === 'text') {
        const a = left.value.toLowerCase();
        const b = right.value.toLowerCase();
        return a === b ? 0 : a < b ? -1 : 1;
    }
    if (left.kind === 'boolean' && right.kind === 'boolean') {
        return left.value === right.value ? 0 : left.value ? 1 : -1;
    }
    return null;
}
/**
 * Where a value sits in a list, under Excel's three match modes.
 *
 * Mode 0 scans for an exact hit. Modes 1 and -1 assume the list is sorted and
 * take the last value that does not pass the target — which is why an unsorted
 * column silently returns the wrong row in every spreadsheet ever written, and
 * why anyone who means "find this" should pass 0.
 */
function matchIn(values, target, mode) {
    if (mode === 0) {
        for (let i = 0; i < values.length; i += 1) {
            const value = values[i];
            if (value && sameValue(value, target))
                return i;
        }
        return null;
    }
    let best = null;
    for (let i = 0; i < values.length; i += 1) {
        const value = values[i];
        if (!value || value.kind === 'blank')
            continue;
        const side = lookupCompare(value, target);
        if (side === null)
            continue;
        if (mode > 0 ? side <= 0 : side >= 0)
            best = i;
        else
            break;
    }
    return best;
}
/** The row of a block whose cell in the given column matches. */
function searchColumn(table, column, target, approximate) {
    const down = [];
    for (let row = 0; row < table.rows; row += 1) {
        down.push(table.values[row * table.columns + column] ?? BLANK);
    }
    return matchIn(down, target, approximate ? 1 : 0);
}
/** The column of a block whose cell in the given row matches. */
function searchRow(table, row, target, approximate) {
    const across = table.values.slice(row * table.columns, (row + 1) * table.columns);
    return matchIn(across, target, approximate ? 1 : 0);
}
/**
 * The rows satisfying every range/criterion pair.
 *
 * SUMIFS, COUNTIFS and AVERAGEIFS differ only in what they do with the answer.
 * Excel requires every range to be the same size as the first, and says so
 * with #VALUE! rather than lining them up by guesswork.
 */
function matchingRows(args, length) {
    if (args.length % 2 !== 0)
        return fail('#VALUE!');
    const pairs = [];
    for (let i = 0; i + 1 < args.length; i += 2) {
        const range = args[i]();
        const values = range.kind === 'range' ? range.values : [range.value];
        if (values.length !== length)
            return fail('#VALUE!');
        pairs.push({ values, criterion: parseCriterion(firstValue(args[i + 1]())) });
    }
    const rows = [];
    for (let row = 0; row < length; row += 1) {
        let all = true;
        for (const pair of pairs) {
            const value = pair.values[row] ?? BLANK;
            if (isError(value))
                return fail(value.code);
            if (!pair.criterion.test(value)) {
                all = false;
                break;
            }
        }
        if (all)
            rows.push(row);
    }
    return ok(rows);
}
function meanOf(numbers) {
    if (numbers.length === 0)
        return errorValue('#DIV/0!');
    const total = numbers.reduce(D.add, D.ZERO);
    const mean = D.divide(total, D.fromInt(numbers.length));
    return mean ? numberValue(mean) : errorValue('#DIV/0!');
}
/** LARGE and SMALL, which differ only in which end they count from. */
function nth(args, end) {
    const range = args[0]();
    const numbers = collectNumbers(range.kind === 'range' ? range.values : [range.value]);
    if (!numbers.ok)
        return errorValue(numbers.code);
    const which = toNumber(firstValue(args[1]()));
    if (!which.ok)
        return errorValue(which.code);
    const at = Math.trunc(D.toNumber(which.value));
    if (at < 1 || at > numbers.value.length)
        return errorValue('#NUM!');
    const sorted = [...numbers.value].sort(D.compare);
    const picked = end === 'smallest' ? sorted[at - 1] : sorted[sorted.length - at];
    return picked ? numberValue(picked) : errorValue('#NUM!');
}
/** CEILING, FLOOR and MROUND, which differ only in the rounding. */
function toMultiple(value, step, how) {
    if (D.isZero(step))
        return how === 'nearest' ? errorValue('#DIV/0!') : numberValue(D.ZERO);
    const quotient = D.divide(value, step);
    if (!quotient)
        return errorValue('#DIV/0!');
    const whole = how === 'up' ? D.ceil(quotient) : how === 'down' ? D.floor(quotient) : D.round(quotient, 0);
    return numberValue(D.multiply(whole, step));
}
/**
 * A float back into the sheet's decimal type.
 *
 * Only the financial functions need this. They are compound-interest formulas
 * whose answers are irrational, so they are computed in floating point and
 * then rounded to a precision no money calculation exceeds. Everything else in
 * the library stays exact.
 */
function fromFloat(value) {
    if (!Number.isFinite(value))
        return errorValue('#NUM!');
    const parsed = D.fromString(value.toFixed(10));
    return parsed ? numberValue(D.normalise(parsed)) : errorValue('#NUM!');
}
/** EDATE and EOMONTH, which differ only in which day of the month they land on. */
function shiftMonths(serial, months, land) {
    const from = serialToDate(serial);
    const year = from.getUTCFullYear();
    const month = from.getUTCMonth() + months;
    if (land === 'month end') {
        // Day zero of the next month is the last day of this one, which also
        // handles February in a leap year without a table.
        return numberValue(D.fromInt(dateToSerial(new Date(Date.UTC(year, month + 1, 0)))));
    }
    // The 31st shifted into a 30-day month lands on the 30th, as Excel does,
    // rather than rolling forward into the next month as raw Date would.
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const day = Math.min(from.getUTCDate(), lastDay);
    return numberValue(D.fromInt(dateToSerial(new Date(Date.UTC(year, month, day)))));
}
//# sourceMappingURL=functions.js.map